// src/cockpit4/components/SmartTrainingSystem.tsx
// ============================================
// 智能培训系统（知识中心 P2-功能8）- v2.0 详细改进计划
// 岗位知识学习路径 / AI 学习计划与测验 / 进度追踪 / 掌握度评估 / 排行榜与证书
// 数据来源：localStorage（xeaos_smart_training）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, BookOpen, Trophy, Award, CheckCircle2, Loader2, Play } from "lucide-react";

interface LearningPath {
  id: string;
  title: string;
  role: string;
  lessons: number;
  completed: number;
  mastery: number;
  progress: number;
  certificate: boolean;
}

const INITIAL_PATHS: LearningPath[] = [
  { id: "lp1", title: "AI 产品经理进阶", role: "产品经理", lessons: 12, completed: 9, mastery: 82, progress: 75, certificate: false },
  { id: "lp2", title: "营销自动化实战", role: "市场运营", lessons: 10, completed: 4, mastery: 55, progress: 40, certificate: false },
  { id: "lp3", title: "AI 销售方法论", role: "销售顾问", lessons: 8, completed: 8, mastery: 91, progress: 100, certificate: true },
  { id: "lp4", title: "数据驱动决策", role: "经营管理者", lessons: 14, completed: 2, mastery: 38, progress: 14, certificate: false },
];

const QUIZ = [
  { q: "XEAOS 的「唯一大脑」原则指什么？", options: ["使用 MySQL 数据库", "Obsidian 作为唯一真相源", "使用 Excel 管理数据", "所有数据存在本地"], answer: 1 },
  { q: "知识飞轮的四个环节是？", options: ["生产-沉淀-使用-反馈", "采集-存储-展示-删除", "提问-回答-结束-归档", "阅读-复制-粘贴-保存"], answer: 0 },
  { q: "五维风险预警雷达包含哪五个维度？", options: ["技术/产品/设计/运营/测试", "财务/客户/运营/市场/人才", "收入/利润/成本/现金/税务", "内部/外部/上游/下游/行业"], answer: 1 },
];

const KEY = "xeaos_smart_training";

function load(): LearningPath[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as LearningPath[];
  } catch { /* ignore */ }
  return INITIAL_PATHS;
}

export default function SmartTrainingSystem() {
  const [paths, setPaths] = useState<LearningPath[]>(load);
  const [quizIdx, setQuizIdx] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const stats = useMemo(() => {
    const totalLessons = paths.reduce((a, p) => a + p.lessons, 0);
    const totalDone = paths.reduce((a, p) => a + p.completed, 0);
    const avgMastery = paths.reduce((a, p) => a + p.mastery, 0) / paths.length;
    const certs = paths.filter((p) => p.certificate).length;
    return { totalLessons, totalDone, avgMastery, certs };
  }, [paths]);

  const startLesson = (id: string) => {
    setPaths((prev) => {
      const next = prev.map((p) => (p.id === id && p.completed < p.lessons ? { ...p, completed: p.completed + 1, progress: Math.min(100, Math.round(((p.completed + 1) / p.lessons) * 100)), mastery: Math.min(100, p.mastery + 3) } : p));
      try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const submitQuiz = () => {
    if (selected === null) return;
    if (selected === QUIZ[quizIdx].answer) setScore((s) => s + 1);
    if (quizIdx < QUIZ.length - 1) { setQuizIdx((i) => i + 1); setSelected(null); }
    else setFinished(true);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <GraduationCap size={14} className="text-indigo-400" />
        <h3 className="text-[12px] font-medium text-white">智能培训系统</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Smart Training</span>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="课程总量" value={`${stats.totalLessons} 节`} color="#94A3B8" />
        <MetricCard label="已完成" value={`${stats.totalDone} 节`} color="#34D399" />
        <MetricCard label="平均掌握度" value={`${stats.avgMastery.toFixed(0)}%`} color="#38BDF8" />
        <MetricCard label="已获证书" value={`${stats.certs} 张`} color="#FBBF24" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen size={11} className="text-indigo-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">岗位学习路径 · 按角色</span>
          </div>
          <div className="space-y-1.5">
            {paths.map((p) => (
              <div key={p.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white">{p.title}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.06] text-gray-400">{p.role}</span>
                  {p.certificate && <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">🏅 已获证书</span>}
                  <span className="ml-auto text-[9px] font-mono text-gray-500">{p.completed}/{p.lessons} 节</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${p.progress}%`, background: p.progress >= 80 ? "#34D399" : "#38BDF8" }} />
                  </div>
                  <span className="text-[9px] font-mono text-gray-400">{p.progress}%</span>
                  <span className="text-[9px] font-mono" style={{ color: p.mastery >= 80 ? "#34D399" : p.mastery >= 50 ? "#FBBF24" : "#F87171" }}>掌握度 {p.mastery}%</span>
                </div>
                <button onClick={() => startLesson(p.id)} className="mt-1.5 flex items-center gap-1 px-2 py-0.5 rounded border border-indigo-500/30 bg-indigo-500/10 text-[8px] font-mono text-indigo-300 hover:bg-indigo-500/20">
                  <Play size={8} /> 学习下一节
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Award size={11} className="text-amber-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">知识测验 · {finished ? "已完成" : `第 ${quizIdx + 1}/${QUIZ.length} 题`}</span>
          </div>
          {!finished ? (
            <div>
              <div className="text-[10px] text-gray-300 leading-relaxed mb-2">{QUIZ[quizIdx].q}</div>
              <div className="space-y-1">
                {QUIZ[quizIdx].options.map((opt, i) => (
                  <button key={i} onClick={() => setSelected(i)} className={`w-full text-left rounded-md border px-2.5 py-1.5 text-[9px] font-mono transition-colors ${selected === i ? "border-indigo-500/50 bg-indigo-500/10 text-indigo-200" : "border-white/[0.05] bg-white/[0.01] text-gray-400 hover:bg-white/[0.03]"}`}>
                    {String.fromCharCode(65 + i)}. {opt}
                  </button>
                ))}
              </div>
              <button onClick={submitQuiz} disabled={selected === null} className="mt-2 px-3 py-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 text-[9px] font-mono text-indigo-300 hover:bg-indigo-500/20 disabled:opacity-40">
                提交答案
              </button>
            </div>
          ) : (
            <div className="text-center py-4">
              <Trophy size={24} className="text-amber-400 mx-auto mb-2" />
              <div className="text-[13px] font-semibold text-white">测验完成</div>
              <div className="text-[10px] font-mono text-gray-400 mt-1">答对 {score}/{QUIZ.length} 题</div>
              <button onClick={() => { setQuizIdx(0); setScore(0); setSelected(null); setFinished(false); }} className="mt-2 px-3 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[9px] font-mono text-gray-400 hover:text-white">
                重新测验
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={11} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">学习排行榜</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { rank: 1, name: "赵经理", score: 960, badge: "🥇" },
            { rank: 2, name: "李女士", score: 885, badge: "🥈" },
            { rank: 3, name: "吴总监", score: 812, badge: "🥉" },
          ].map((u) => (
            <div key={u.rank} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <span className="text-[12px]">{u.badge}</span>
              <span className="text-gray-300">{u.name}</span>
              <span className="ml-auto text-indigo-300">{u.score} 分</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

